import React, {
  useState,
  useEffect,
  useRef,
  ReactNode,
  useMemo,
  useCallback,
} from "react";
import Icon from "./Icon";
import cn from "./utils/cn";
import Logo from "./Logo";
import {
  componentMaxHeight,
  HEADER_BOTTOM_MARGIN,
  HEADER_HEIGHT,
} from "./utils/heights";
import { HeaderLinks } from "./Header/HeaderLinks";
import { throttle } from "es-toolkit/compat";
import { Theme } from "./styles/colors/types";
import { COLLAPSE_TRIGGER_DISTANCE } from "./Notice/component";
import { useThemedScrollpoints } from "./hooks/use-themed-scrollpoints";
import { ThemedScrollpoint } from "./Header/types";

export type { ThemedScrollpoint };

/**
 * Represents the state of the user session in the header.
 */
export type HeaderSessionState = {
  /**
   * Indicates if the user is signed in.
   */
  signedIn: boolean;

  /**
   * Information required to log out the user.
   */
  logOut: {
    /**
     * Token used for logging out.
     */
    token: string;

    /**
     * URL to log out the user.
     */
    href: string;
  };

  /**
   * Name of the user's account.
   */
  accountName: string;
};

/**
 * Props for the Header component.
 */
export type HeaderProps = {
  /**
   * Optional classnames to add to the header
   */
  className?: string;
  /**
   * Indicates if the notice banner is enabled.
   */
  isNoticeBannerEnabled?: boolean;
  /**
   * Height of the notice banner in pixels.
   */
  noticeHeight?: number;
  /**
   * Optional search bar element.
   */
  searchBar?: ReactNode;

  /**
   * Optional search button element.
   */
  searchButton?: ReactNode;

  /**
   * URL for the logo link.
   */
  logoHref?: string;

  /**
   * Array of header links.
   */
  headerLinks?: {
    /**
     * URL for the link.
     */
    href: string;

    /**
     * Label for the link.
     */
    label: string;

    /**
     * Indicates if the link should open in a new tab.
     */
    external?: boolean;
  }[];

  /**
   * Optional classname for styling the header links container.
   */
  headerLinksClassName?: string;

  /**
   * Optional classname for styling the header center container.
   */
  headerCenterClassName?: string;

  /**
   * Optional desktop navigation element.
   */
  nav?: ReactNode;

  /**
   * Optional mobile navigation element.
   */
  mobileNav?: ReactNode;

  /**
   * State of the user session.
   */
  sessionState?: HeaderSessionState;

  /**
   * Array of themed scrollpoints. The header will change its appearance based on the scrollpoint in view.
   */
  themedScrollpoints?: ThemedScrollpoint[];

  /**
   * Visibility setting for the search button.
   * - "all": Visible on all devices.
   * - "desktop": Visible only on desktop devices.
   * - "mobile": Visible only on mobile devices.
   */
  searchButtonVisibility?: "all" | "desktop" | "mobile";

  /**
   * Optional location object to detect location changes.
   */
  location?: Location;

  /**
   * Optional badge text to display on the logo.
   */
  logoBadge?: string;
};

const FLEXIBLE_DESKTOP_CLASSES = "hidden md:flex flex-1 items-center h-full";

/**
 * Maximum width before the menu expanded into full width
 */
const MAX_MOBILE_MENU_WIDTH = "560px";

const Header: React.FC<HeaderProps> = ({
  className,
  isNoticeBannerEnabled = false,
  noticeHeight = 0,
  searchBar,
  searchButton,
  logoHref,
  headerLinks,
  headerLinksClassName,
  headerCenterClassName,
  nav,
  mobileNav,
  sessionState,
  themedScrollpoints = [],
  searchButtonVisibility = "all",
  location,
  logoBadge,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [fadingOut, setFadingOut] = useState(false);
  const [noticeBannerVisible, setNoticeBannerVisible] = useState(
    isNoticeBannerEnabled,
  );
  const menuRef = useRef<HTMLDivElement>(null);
  const scrollpointClasses = useThemedScrollpoints(themedScrollpoints);

  const headerStyle = {
    height: HEADER_HEIGHT,
    top: noticeBannerVisible ? `${noticeHeight}px` : "0",
  };

  const headerClassName = cn(
    "fixed left-0 top-0 w-full z-50 bg-neutral-000 dark:bg-neutral-1300 border-b border-neutral-300 dark:border-neutral-1000 transition-all duration-300 ease-in-out",
    scrollpointClasses,
    {
      "md:top-auto": noticeBannerVisible,
    },
  );

  const closeMenu = () => {
    setFadingOut(true);
  };

  useEffect(() => {
    if (!fadingOut) return;
    const id = setTimeout(() => {
      setShowMenu(false);
      setFadingOut(false);
    }, 150);
    return () => clearTimeout(id);
  }, [fadingOut]);

  const handleNoticeClose = useCallback(() => {
    setNoticeBannerVisible(false);
  }, []);

  useEffect(() => {
    document.addEventListener("notice-closed", handleNoticeClose);
    return () =>
      document.removeEventListener("notice-closed", handleNoticeClose);
  }, [handleNoticeClose]);

  useEffect(() => {
    if (!isNoticeBannerEnabled) {
      return;
    }

    const noticeElement = document.querySelector('[data-id="ui-notice"]');

    if (!noticeElement) {
      console.warn("Header: Notice element not found");
      return;
    }

    let previousVisibility = noticeBannerVisible;

    const handleScroll = () => {
      const scrollY = window.scrollY;
      const isNoticeHidden = noticeElement.classList.contains(
        "ui-announcement-hidden",
      );

      const shouldBeVisible =
        scrollY <= COLLAPSE_TRIGGER_DISTANCE && !isNoticeHidden;

      if (shouldBeVisible !== previousVisibility) {
        previousVisibility = shouldBeVisible;
        setNoticeBannerVisible(shouldBeVisible);
      }
    };

    const throttledHandleScroll = throttle(handleScroll, 100);

    handleScroll();

    window.addEventListener("scroll", throttledHandleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", throttledHandleScroll);
    };
  }, [isNoticeBannerEnabled, noticeBannerVisible]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1040) {
        setShowMenu(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (showMenu) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, [showMenu]);

  // Close menu when location changes
  const [prevLocation, setPrevLocation] = useState(location);
  if (prevLocation !== location) {
    setPrevLocation(location);
    if (location && showMenu) {
      closeMenu();
    }
  }

  const wrappedSearchButton = useMemo(
    () =>
      searchButton ? (
        <div className="text-neutral-1300 dark:text-neutral-000 flex items-center">
          {searchButton}
        </div>
      ) : null,
    [searchButton],
  );

  return (
    <>
      <header role="banner" style={headerStyle} className={headerClassName}>
        <div className={cn("flex items-center h-full", className)}>
          <nav className="flex flex-1 h-full items-center">
            {(["light", "dark"] as Theme[]).map((theme) => (
              <Logo
                key={theme}
                href={logoHref}
                theme={theme}
                badge={logoBadge}
                additionalLinkAttrs={{
                  className: cn("h-full focus-base rounded mr-4 lg:mr-8", {
                    "flex dark:hidden": theme === "light",
                    "hidden dark:flex": theme === "dark",
                  }),
                }}
              />
            ))}
            <div className={FLEXIBLE_DESKTOP_CLASSES}>{nav}</div>
          </nav>
          <div className="flex md:hidden flex-1 items-center justify-end gap-6 h-full">
            {searchButtonVisibility !== "desktop" ? wrappedSearchButton : null}
            <button
              className="cursor-pointer focus-base rounded flex items-center p-0"
              onClick={() => setShowMenu(!showMenu)}
              aria-expanded={showMenu}
              aria-controls="mobile-menu"
              aria-label="Toggle menu"
            >
              <Icon
                name={
                  showMenu
                    ? "icon-gui-x-mark-outline"
                    : "icon-gui-bars-3-outline"
                }
                additionalCSS="text-neutral-1300 dark:text-neutral-000"
                size="1.5rem"
              />
            </button>
          </div>
          {searchBar ? (
            <div
              className={cn(
                FLEXIBLE_DESKTOP_CLASSES,
                "justify-center",
                headerCenterClassName,
              )}
            >
              {searchBar}
            </div>
          ) : null}
          <HeaderLinks
            className={cn(FLEXIBLE_DESKTOP_CLASSES, headerLinksClassName)}
            headerLinks={headerLinks}
            sessionState={sessionState}
            searchButton={wrappedSearchButton}
            searchButtonVisibility={searchButtonVisibility}
          />
        </div>
      </header>
      {showMenu ? (
        <>
          <div
            className={cn(
              "fixed inset-0 bg-neutral-1300 dark:bg-neutral-1300 z-40",
              {
                "animate-[fade-in-ten-percent_150ms_ease-in-out_forwards]":
                  !fadingOut,
                "animate-[fade-out-ten-percent_150ms_ease-in-out_forwards]":
                  fadingOut,
              },
            )}
            onClick={closeMenu}
            onKeyDown={(e) => e.key === "Escape" && closeMenu()}
            role="presentation"
          />
          <div
            id="mobile-menu"
            className="md:hidden fixed flex flex-col top-[4.75rem] overflow-y-hidden mx-3 right-0 w-[calc(100%-24px)] bg-neutral-000 dark:bg-neutral-1300 rounded-2xl ui-shadow-lg-medium z-50"
            style={{
              maxWidth: MAX_MOBILE_MENU_WIDTH,
              maxHeight: componentMaxHeight(
                HEADER_HEIGHT,
                HEADER_BOTTOM_MARGIN,
              ),
            }}
            ref={menuRef}
            role="navigation"
          >
            {mobileNav}
            <HeaderLinks
              headerLinks={headerLinks}
              sessionState={sessionState}
            />
          </div>
        </>
      ) : null}
    </>
  );
};

export default Header;
